import { Button, DatePicker, Input, Select, TableProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import ExportIcon from "../../../../../assets/icons/ExportIcon";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../../assets/icons/SelectDownIcon";
import StatusItem from "../../../../../components/common/StatusItem";
import CustomTable from "../../../../../components/ui/CustomTable";
import { API_URL } from "../../../../../constants/apiUrl";
import {
  AUTH_MANAGE_API,
  dbDateFormat,
} from "../../../../../constants/constants";
import {
  AllAttendanceParams,
  DailyAttendanceSettingsResponse,
} from "../../../../../features/organization/contractual/AttendanceManagement/attendanceManagement.type";
import { useGetDailyAttendanceListQuery } from "../../../../../features/organization/contractual/AttendanceManagement/attendanceManagementApi";
import useDownloadEmployeeXlsx from "../../../../../hooks/useDownloadExcelWithApi";
import useError from "../../../../../hooks/useError";
import useTable from "../../../../../hooks/useTable";
import { formatDate } from "../../../../../lib/time";
import { countParPageSerialNumber } from "../../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../../../utils/utils";
import CalanderIcon from "../../../survey/templates/_icons/CalanderIcon";

const { RangePicker } = DatePicker;

const STATUES = [
  {
    label: "Late",
    value: "late",
    className: "status rounded-lg status-warning",
  },
  {
    label: "Present",
    value: "present",
    className: "status rounded-lg status-success",
  },
  {
    label: "Absent",
    value: "absent",
    className: "status rounded-lg status-danger",
  },
  // {
  //   label: "Processing",
  //   value: "processing",
  //   className: "status rounded-lg status-link",
  // },
];

export const formatTime = (timeString?: string) => {
  if (!timeString) return "-";
  return moment(timeString, "HH:mm:ss").format("hh:mm A");
};

const DailyAttendance = () => {
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllAttendanceParams>();

  // ------ Queries ------
  const { data, isLoading, isFetching, isError, error } =
    useGetDailyAttendanceListQuery({
      params: {
        ...filter,
        search: keyword,
        limit,
      },
    });

  // ------ Errors ------
  useError(isError, error, "Get daily attendance list failed");

  // ---------- Excel Download Function   ------------
  const handleDownload = () => {
    downloadXlsx(
      `${AUTH_MANAGE_API + API_URL.getDailyAttendanceList}`,
      {
        ...filter,
        page: page,
        search: keyword,
        skip: 0,
        limit: 1000000000000000,
      },
      `Daily_Attendance_${moment().format("DD MMMM YYYY")}_${moment().format(
        "h.mm.ss A"
      )}`,
      "attendances",
      (item: DailyAttendanceSettingsResponse["attendances"][0]) => {
        return {
          "Bondhu Name": item?.member?.name || "-",
          "Bondhu ID": item?.member?.employee_id || "-",
          "Shift Time": item?.shift
            ? `${formatTime(item?.shift?.shift_start_time)} - ${formatTime(
                item?.shift?.shift_end_time
              )}`
            : "-",
          Dates:
            item?.attendance_date !== undefined &&
            item?.attendance_date !== null
              ? formatDate(item?.attendance_date)
              : "-",
          "Check-in": item?.check_in_time
            ? `${formatTime(item?.check_in_time)}`
            : "-",
          "	Check-out": item?.check_out_time
            ? `${formatTime(item?.check_out_time)}`
            : "-",
          Status: capitalize(item?.status) || "-",
        };
      }
    );
  };

  // ----- Table Columns ------
  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Bondhu Name",
      dataIndex: "member",
      render: (val) => valueOrNa(val?.name),
    },
    {
      align: "center",
      title: "Bondhu ID",
      dataIndex: "member",
      render: (val: any) => (val?.employee_id ? val.employee_id : "n/a"),
    },
    {
      title: "Job Title",
      dataIndex: "jobApply",
      render: (val: any) => valueOrNa(val?.job?.job_title),
    },
    {
      align: "center",
      title: "Shift Time",
      dataIndex: "shift",
      render: (val) =>
        `${formatTime(val?.shift_start_time)} - ${formatTime(
          val?.shift_end_time
        )}`,
    },
    {
      align: "center",
      title: "Dates",
      dataIndex: "attendance_date",
      render: (val) => formatDate(val),
    },
    {
      align: "center",
      title: "Check-in",
      dataIndex: "check_in_time",
      render: (val) => formatTime(val),
    },
    {
      align: "center",
      title: "Check-out",
      dataIndex: "check_out_time",
      render: (val) => formatTime(val),
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "status",
      render: (val) => <StatusItem value={val} list={STATUES} />,
    },
  ];

  return (
    <>
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total || 0}
        dataSource={addKeyInArray(data?.data?.attendances || [])}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              filterOption={selectSearchOption}
              options={selectArrayOption(STATUES, "label", "value")}
              onChange={(value) => handleFilter("status", value)}
            />

            <RangePicker
              style={{ width: 245 }}
              separator="-"
              className="w-full input-bordered"
              format={"DD/MM/YYYY"}
              suffixIcon={<CalanderIcon />}
              placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
              onChange={(dates: (Dayjs | null)[] | null) => {
                const [start, end] = dates || [];

                // Call filter only if both dates are selected
                if (start && end) {
                  handleFilter("from_date", dayjs(start).format(dbDateFormat));
                  handleFilter("to_date", dayjs(end).format(dbDateFormat));
                } else {
                  // Clear filters if user clears dates
                  handleFilter("from_date", undefined);
                  handleFilter("to_date", undefined);
                }
              }}
            />

            <Button
              type="primary"
              onClick={handleDownload}
              loading={excelLoading}
            >
              <ExportIcon /> Export
            </Button>
          </>
        }
      />
    </>
  );
};

export default DailyAttendance;
